import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

// Global audio management
const audioPlayers = new Set();

const pauseAllOtherPlayers = (currentPlayer) => {
  audioPlayers.forEach(player => {
    if (player !== currentPlayer && player.audio && !player.audio.paused) {
      player.audio.pause();
      player.setIsPlaying(false);
    }
  });
};

/**
 * A clean, compact audio player component with drag-to-seek functionality
 * @param {string} audioUrl - URL to the audio file
 * @param {string} duration - Formatted duration string
 * @param {object} sx - Additional styling
 */
const TinyAudioPlayer = ({ 
  audioUrl,
  duration = '0:00',
  sx = {} 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [isDragging, setIsDragging] = useState(false);
  const [wasPlayingBeforeDrag, setWasPlayingBeforeDrag] = useState(false);
  const [hasError, setHasError] = useState(false);
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const playerRef = useRef({ audio: null, setIsPlaying });
  const isDraggingRef = useRef(false);
  isDraggingRef.current = isDragging;
  
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${min}:${sec}`;
  };
  
  // Check if URL is valid
  const isValidAudioUrl = (url) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };
  
  // Initialize audio when component mounts
  useEffect(() => {
    if (!audioUrl) {
      setHasError(true);
      return;
    }

    if (!isValidAudioUrl(audioUrl)) {
      setHasError(true);
      return;
    }

    setHasError(false);
    let disposed = false;
    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    playerRef.current.audio = audio;

    audioPlayers.add(playerRef.current);

    const onLoadedMetadata = () => {
      if (disposed) return;
      setHasError(false);
    };

    const onError = () => {
      if (disposed) return;
      // Clearing the element during teardown often reports MEDIA_ERR_ABORTED (1) / networkState 3
      const code = audio.error?.code;
      if (!audio.src || code === 1 /* MEDIA_ERR_ABORTED */) {
        return;
      }
      setHasError(true);
    };

    const onTimeUpdate = () => {
      if (disposed || isDraggingRef.current) return;
      const current = audio.currentTime;
      const dur = audio.duration || 0;

      if (isFinite(current) && isFinite(dur) && dur > 0) {
        setCurrentTime(formatTime(current));
        setProgress((current / dur) * 100);
      }
    };

    const onSeeking = () => {};
    const onSeeked = () => {};

    const onEnded = () => {
      if (disposed) return;
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime('0:00');
    };

    const onPause = () => {
      if (disposed) return;
      setIsPlaying(false);
    };

    const onPlay = () => {
      if (disposed) return;
      setIsPlaying(true);
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('error', onError);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('seeking', onSeeking);
    audio.addEventListener('seeked', onSeeked);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);

    audio.volume = 0.8;
    audio.preload = 'auto';

    return () => {
      disposed = true;
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('seeking', onSeeking);
      audio.removeEventListener('seeked', onSeeked);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);

      audioPlayers.delete(playerRef.current);
      playerRef.current.audio = null;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();

      if (audioRef.current === audio) {
        audioRef.current = null;
      }
    };
  }, [audioUrl]);
  
  const togglePlay = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (isPlaying) {
      audio.pause();
    } else {
      // Pause all other players before starting this one
      pauseAllOtherPlayers(playerRef.current);
      
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise
          .catch((error) => {
            setIsPlaying(false);
            if (error.name === 'NotAllowedError') {
              setTimeout(() => {
                audio.play().catch(() => setIsPlaying(false));
              }, 100);
            }
          });
      }
    }
  };

  const handleProgressClick = (event) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration || !isFinite(audio.duration)) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickPercent = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
    const seekTime = (clickPercent / 100) * audio.duration;
    
    if (isFinite(seekTime) && seekTime >= 0) {
      audio.currentTime = seekTime;
      setProgress(clickPercent);
      setCurrentTime(formatTime(seekTime));
    }
  };

  const handleMouseDown = (event) => {
    setIsDragging(true);
    setWasPlayingBeforeDrag(isPlaying);
    
    // Pause audio during dragging
    const audio = audioRef.current;
    if (audio && isPlaying) {
      audio.pause();
    }
    
    handleProgressClick(event);
  };

  const handleMouseMove = (event) => {
    if (!isDragging) return;
    
    const audio = audioRef.current;
    if (!audio || !audio.duration || !isFinite(audio.duration)) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const mouseX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
    const clickPercent = Math.max(0, Math.min(100, (mouseX / rect.width) * 100));
    
    setProgress(clickPercent);
    setCurrentTime(formatTime((clickPercent / 100) * audio.duration));
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    const audio = audioRef.current;
    if (!audio || !audio.duration || !isFinite(audio.duration)) return;

    const seekTime = (progress / 100) * audio.duration;
    if (isFinite(seekTime) && seekTime >= 0) {
      audio.currentTime = seekTime;
      
      // Resume playing if it was playing before dragging
      if (wasPlayingBeforeDrag) {
        // Pause all other players before resuming this one
        pauseAllOtherPlayers(playerRef.current);
        audio.play().catch(() => {});
      }
    }
    
    setIsDragging(false);
    setWasPlayingBeforeDrag(false);
  };

  useEffect(() => {
    if (isDragging) {
      // Throttle mouse move events to reduce sliding speed
      let timeoutId;
      const throttledMouseMove = (event) => {
        if (timeoutId) return;
        timeoutId = setTimeout(() => {
          handleMouseMove(event);
          timeoutId = null;
        }, 16); // ~60fps
      };

      document.addEventListener('mousemove', throttledMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', throttledMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [isDragging, progress]);
  
  return (
    <Box sx={{ 
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      ...sx
    }}>
      {/* Play/Pause Button */}
      <Box 
        onClick={hasError ? undefined : togglePlay}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: hasError ? '#ccc' : '#2E7D32', // Gray if error, green if normal
          color: 'white',
          borderRadius: '50%',
          width: 32,
          height: 32,
          cursor: hasError ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          flexShrink: 0,
          border: '2px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          opacity: hasError ? 0.6 : 1,
          '&:hover': {
            bgcolor: hasError ? '#ccc' : '#1B5E20',
            transform: hasError ? 'none' : 'scale(1.05)',
            boxShadow: hasError ? '0 2px 4px rgba(0,0,0,0.1)' : '0 4px 8px rgba(0,0,0,0.2)'
          }
        }}
      >
        {hasError ? (
          <Typography sx={{ fontSize: 12, color: '#666' }}>!</Typography>
        ) : isPlaying ? (
          <PauseIcon sx={{ fontSize: 18 }} />
        ) : (
          <PlayArrowIcon sx={{ fontSize: 18, ml: 0.5 }} />
        )}
      </Box>
      
      {/* Progress Bar */}
      <Box 
        ref={progressBarRef}
        sx={{ 
          flex: 1,
          position: 'relative',
          cursor: hasError ? 'not-allowed' : 'pointer',
          minWidth: 80,
          maxWidth: 120,
          opacity: hasError ? 0.6 : 1
        }}
        onMouseDown={hasError ? undefined : handleMouseDown}
      >
        {/* Background track */}
        <Box
          sx={{
            width: '100%',
            height: 4,
            bgcolor: '#E0E0E0',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          {/* Progress fill */}
          <Box
            sx={{
              width: hasError ? '0%' : `${progress}%`,
              height: '100%',
              bgcolor: '#2E7D32',
              transition: isDragging ? 'none' : 'width 0.1s ease',
              borderRadius: 2
            }}
          />
        </Box>
        
        {/* Progress handle */}
        {!hasError && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${progress}%`,
              transform: 'translate(-50%, -50%)',
              width: 10,
              height: 10,
              bgcolor: '#2E7D32',
              borderRadius: '50%',
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              transition: isDragging ? 'none' : 'left 0.1s ease',
              '&:hover': {
                transform: 'translate(-50%, -50%) scale(1.1)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.4)'
              }
            }}
          />
        )}
      </Box>
      
      {/* Time Display */}
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ 
          fontSize: '0.75rem',
          fontWeight: 500,
          minWidth: 70,
          textAlign: 'right', // Changed from 'left' to 'right'
          flexShrink: 0,
          color: hasError ? '#999' : '#666666'
        }}
      >
        {hasError ? 'Audio unavailable' : `${currentTime} / ${duration}`}
      </Typography>
    </Box>
  );
};

export default TinyAudioPlayer;
